import React from "react";
import { Link } from "react-router-dom";

const styles = {
  container: {
    maxWidth: "720px",
    margin: "0 auto",
    padding: "40px 24px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    lineHeight: "1.7",
    color: "#333",
    backgroundColor: "#fafafa",
    minHeight: "100vh",
  } as React.CSSProperties,
  header: {
    textAlign: "center" as const,
    marginBottom: "48px",
    paddingBottom: "32px",
    borderBottom: "2px solid #e0e0e0",
  } as React.CSSProperties,
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: "0 0 8px 0",
  } as React.CSSProperties,
  subtitle: {
    fontSize: "1rem",
    color: "#666",
    margin: 0,
  } as React.CSSProperties,
  section: {
    marginBottom: "40px",
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: "1.4rem",
    fontWeight: 600,
    color: "#2c5282",
    marginBottom: "16px",
    paddingBottom: "8px",
    borderBottom: "1px solid #cbd5e0",
  } as React.CSSProperties,
  contactBox: {
    backgroundColor: "#ebf8ff",
    border: "1px solid #90cdf4",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "32px",
  } as React.CSSProperties,
  contactEmail: {
    color: "#2b6cb0",
    fontWeight: 600,
    textDecoration: "none",
  } as React.CSSProperties,
  faqItem: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  } as React.CSSProperties,
  question: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#2d3748",
    marginBottom: "12px",
  } as React.CSSProperties,
  answer: {
    color: "#4a5568",
    margin: 0,
  } as React.CSSProperties,
  list: {
    margin: "12px 0",
    paddingLeft: "24px",
    color: "#4a5568",
  } as React.CSSProperties,
  knownIssuesBox: {
    backgroundColor: "#fffaf0",
    border: "1px solid #fbd38d",
    borderRadius: "8px",
    padding: "20px",
  } as React.CSSProperties,
  footer: {
    marginTop: "48px",
    paddingTop: "24px",
    borderTop: "1px solid #e0e0e0",
    textAlign: "center" as const,
    color: "#666",
    fontSize: "0.9rem",
  } as React.CSSProperties,
  link: {
    color: "#2b6cb0",
    textDecoration: "none",
  } as React.CSSProperties,
};

const MomentalSupport: React.FC = () => {
  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Momental: Maze Puzzles</h1>
        <p style={styles.subtitle}>Support & Help Center</p>
      </header>

      <div style={styles.contactBox}>
        <p style={{ margin: "0 0 8px 0", fontWeight: 500 }}>
          Need help? We're here for you.
        </p>
        <p style={{ margin: 0 }}>
          For support, feedback, or bug reports, email us at{" "}
          <a href="mailto:MomentalFeedback@gmail.com" style={styles.contactEmail}>
            MomentalFeedback@gmail.com
          </a>
        </p>
        <p style={{ margin: "8px 0 0 0", fontSize: "0.9rem", color: "#4a5568" }}>
          We read every message.
        </p>
      </div>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>

        <div style={styles.faqItem}>
          <p style={styles.question}>What kind of game is Momental?</p>
          <p style={styles.answer}>
            Momental is a puzzle maze game built around momentum and planning. A
            swipe sends the ball sliding until it hits something—you plan where it
            lands, not where it steps. Many puzzles also let you change the board
            itself to redirect motion and discover new solutions.
          </p>
        </div>

        <div style={styles.faqItem}>
          <p style={styles.question}>Can I play offline?</p>
          <p style={styles.answer}>
            <strong>Yes.</strong> All built-in levels can be played fully offline.
            Any community levels you have already downloaded can also be played
            offline. An internet connection is only required to browse or upload
            community levels.
          </p>
        </div>

        <div style={styles.faqItem}>
          <p style={styles.question}>How does progress syncing work?</p>
          <p style={styles.answer}>
            Your progress is automatically synced across your devices using Apple
            iCloud (CloudKit), if iCloud is enabled on your device. Progress data is
            managed entirely by Apple. We do not have server-side access to your
            save data.
          </p>
        </div>

        <div style={styles.faqItem}>
          <p style={styles.question}>What are community levels?</p>
          <p style={styles.answer}>
            Momental includes an in-game level editor that lets players create and
            share their own puzzles. Uploading levels is optional. If you choose to
            upload levels, Game Center is used to uniquely identify contributors.
            You can customize your display name, or choose not to upload levels at
            all.
          </p>
        </div>

        <div style={styles.faqItem}>
          <p style={styles.question}>Does the game use Game Center?</p>
          <p style={styles.answer}>
            <strong>Yes, optionally.</strong> Game Center is used only for
            identifying players who choose to upload community levels. There are no
            required accounts or logins to play the game.
          </p>
        </div>

        <div style={styles.faqItem}>
          <p style={styles.question}>Is Momental suitable for children?</p>
          <p style={styles.answer}>
            Momental contains no ads and no objectionable content. While it is
            suitable for younger players, it is not specifically designed as a
            children's app. Community level names are filtered to prevent
            inappropriate language.
          </p>
        </div>

        <div style={styles.faqItem}>
          <p style={styles.question}>How do I report a bug or suggest a feature?</p>
          <div style={styles.answer}>
            <p style={{ margin: "0 0 8px 0" }}>
              Please email{" "}
              <a
                href="mailto:MomentalFeedback@gmail.com"
                style={styles.contactEmail}
              >
                MomentalFeedback@gmail.com
              </a>{" "}
              with:
            </p>
            <ul style={styles.list}>
              <li>Your device model</li>
              <li>iOS version</li>
              <li>A brief description of the issue</li>
            </ul>
            <p style={{ margin: 0 }}>
              Screenshots or screen recordings are helpful if available.
            </p>
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Known Issues</h2>
        <div style={styles.knownIssuesBox}>
          <ul style={{ ...styles.list, margin: 0 }}>
            <li>
              Community levels require an internet connection to browse or upload.
            </li>
            <li>Progress sync depends on iCloud availability and settings.</li>
          </ul>
          <p style={{ margin: "12px 0 0 0", color: "#744210" }}>
            If you encounter anything unexpected, please let us know.
          </p>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Community</h2>
        <p style={styles.answer}>
          We may add a community space (such as Discord) in the future. For now,
          feedback and discussion are handled via email.
        </p>
      </section>

      <footer style={styles.footer}>
        <p>
          <a href="https://momental.craftword.game/Privacy" style={styles.link}>
            Privacy Policy
          </a>
          {" | "}
          <a href="https://momental.craftword.game" style={styles.link}>
            Home
          </a>
        </p>
        <p style={{ marginTop: "8px" }}>© 2026 Momental</p>
      </footer>
    </div>
  );
};

export default MomentalSupport;
